import { useEffect, useMemo, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import toast from "react-hot-toast";
import {
  fetchCompanies,
  fetchEmailTemplateConfig,
  fetchEmailTemplates,
  fetchInvites,
  fetchSurveyTemplates,
  fetchWorkspace,
  resendInviteEmails,
  saveCompany,
  sendBatchOnboarding,
} from "../api/adminClient";
import { fetchInsights } from "../api/analyticsClient";
import { buildInviteWizardContext } from "./inviteAssistantContext";
import BulkInviteModal from "./BulkInviteModal";
import InviteRoleField from "./InviteRoleField";
import DuplicateEmailConfirmDialog from "./DuplicateEmailConfirmDialog";
import WizardInviteEmailSection from "./WizardInviteEmailSection";
import TemplatePreviewPanel from "./TemplatePreviewPanel";
import InviteAssessmentOptions from "./InviteAssessmentOptions";
import { LENS_ROUTES } from "./routes";
import {
  assessmentFormatRequiredError,
  participantAssessmentUrl,
  rolesFromWorkspace,
  sampleCsvForWorkspace,
  workspaceLiteInviteAvailable,
  workspaceInviteDefaults,
  isFlatWorkspace,
  flatWorkspaceCompany,
} from "./inviteConstants";
import {
  ASSESSMENT_MODE_FULL_ONLY,
  DEFAULT_ASSESSMENT_MODE,
  DEFAULT_OFFER_LITE_REPORT,
} from "./inviteOptions";
import { uploadCompanyLogo } from "./uploadCompanyLogo";
// Only the subject constants are still used here — the hardcoded HTML bodies in
// this module are no longer part of the wizard's send path (templates now come
// from the DB). The module stays for ActionModePanel's copilot flow.
import {
  inviteSubjectForPack,
} from "./inviteEmailTemplates";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const emptyParticipant = (defaultRole = "Leadership Team") => ({
  full_name: "",
  email: "",
  emailError: "",
  designation: defaultRole,
});

function splitFullName(fullName) {
  const trimmed = fullName.trim();
  if (!trimmed) return { first_name: "", last_name: "" };
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return { first_name: parts[0], last_name: "" };
  return { first_name: parts[0], last_name: parts.slice(1).join(" ") };
}

function validateParticipantEmail(email) {
  const value = email.trim();
  if (!value) return "Email is required";
  if (value.startsWith(" ") || value.endsWith(" ")) {
    return "Email cannot have leading or trailing spaces";
  }
  if (!EMAIL_RE.test(value)) return "Enter a valid email address";
  return "";
}

function isParticipantRowValid(p) {
  return (
    !!p.full_name.trim() &&
    !!p.designation &&
    !validateParticipantEmail(p.email)
  );
}

export default function EngagementWizard({ embedded = false }) {
  const outletContext = useOutletContext();
  const setAdminAssistantContext = outletContext?.setAdminAssistantContext;
  const [step, setStep] = useState(1);
  const [allInvites, setAllInvites] = useState([]);
  const [invitesLoading, setInvitesLoading] = useState(true);
  const [insightsOrganizations, setInsightsOrganizations] = useState([]);
  const [insightsLoading, setInsightsLoading] = useState(true);
  const [companyMode, setCompanyMode] = useState("existing");
  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(true);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [company, setCompany] = useState({
    company_name: "",
    company_logo: "",
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [participants, setParticipants] = useState([emptyParticipant()]);
  const [preSurvey, setPreSurvey] = useState("N");
  const [assessmentMode, setAssessmentMode] = useState(DEFAULT_ASSESSMENT_MODE);
  const [offerLiteReport, setOfferLiteReport] = useState(DEFAULT_OFFER_LITE_REPORT);
  const [loading, setLoading] = useState(false);
  const [step1Saving, setStep1Saving] = useState(false);
  const [result, setResult] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [newCompanyDraft, setNewCompanyDraft] = useState({
    company_name: "",
    company_logo: "",
  });

  const [editorHtml, setEditorHtml] = useState("");
  // Default is NO override: the backend picks a template per invite from the
  // recipient's audience. Only an explicit edit in the customize editor flips
  // this, which then applies one body to the whole batch.
  const [emailCustomized, setEmailCustomized] = useState(false);
  const [emailExpanded, setEmailExpanded] = useState(false);
  const [dbTemplates, setDbTemplates] = useState([]);
  const [dbAudiences, setDbAudiences] = useState([]);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [pendingSendInvites, setPendingSendInvites] = useState(null);
  const [duplicateConfirm, setDuplicateConfirm] = useState(null);
  const [copiedInviteId, setCopiedInviteId] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [inviteBlockMessage, setInviteBlockMessage] = useState("");

  const inviteRoles = useMemo(() => rolesFromWorkspace(workspace), [workspace]);
  const defaultInviteRole = inviteRoles[0] || "Leadership Team";
  const sampleCsv = useMemo(() => sampleCsvForWorkspace(workspace), [workspace]);
  const litePathAvailable = useMemo(
    () => workspaceLiteInviteAvailable(workspace),
    [workspace]
  );
  const flatWorkspace = useMemo(() => isFlatWorkspace(workspace), [workspace]);

  useEffect(() => {
    if (!flatWorkspace) return;
    const dc = flatWorkspaceCompany(workspace);
    if (!dc) return;
    setSelectedCompanyId(dc.id);
    setCompany({
      company_name: dc.company_name,
      company_logo: dc.company_logo || "",
    });
    setCompanyMode("existing");
    setStep(2);
  }, [flatWorkspace, workspace]);

  useEffect(() => {
    if (!workspace) return;
    const defs = workspaceInviteDefaults(workspace);
    if (!litePathAvailable) {
      setAssessmentMode(ASSESSMENT_MODE_FULL_ONLY);
      setOfferLiteReport("N");
      return;
    }
    setAssessmentMode(defs.assessment_mode);
    setOfferLiteReport(defs.offer_lite_report);
  }, [litePathAvailable, workspace]);

  const roleSource = pendingSendInvites
    ? pendingSendInvites.map((inv) => ({ designation: inv.designation }))
    : participants;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchWorkspace();
        if (!cancelled) setWorkspace(res.data?.workspace || null);
      } catch {
        if (!cancelled) setWorkspace(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Align empty draft rows to workspace default role once designations load.
  useEffect(() => {
    setParticipants((rows) => {
      if (rows.length !== 1) return rows;
      const only = rows[0];
      if (only.full_name || only.email) return rows;
      if (only.designation === defaultInviteRole) return rows;
      return [{ ...only, designation: defaultInviteRole }];
    });
  }, [defaultInviteRole]);

  // Saved templates + audience rules from the admin console, so this step
  // shows (and sends) what an admin actually configured there.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [tplRes, cfgRes] = await Promise.all([
          fetchEmailTemplates("invite"),
          fetchEmailTemplateConfig(),
        ]);
        if (cancelled) return;
        setDbTemplates(tplRes.data?.templates || []);
        setDbAudiences(cfgRes.data?.audiences || []);
      } catch {
        // Non-fatal: the send still works (backend resolves templates itself),
        // the admin just loses the per-audience summary in this step.
        if (!cancelled) setDbTemplates([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const copyLink = async (link, inviteId) => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopiedInviteId(inviteId);
      window.setTimeout(() => setCopiedInviteId(null), 2000);
    } catch {
      toast.error("Could not copy link");
    }
  };

  const loadCompanies = async (options = {}) => {
    const { selectOrgId = null, selectByName = null } = options;
    setCompaniesLoading(true);
    try {
      const res = await fetchCompanies();
      const list = res.data.companies || [];
      setCompanies(list);

      if (list.length === 0) {
        setCompanyMode("new");
        return list;
      }

      let picked = null;
      if (selectOrgId) {
        picked = list.find((c) => c.id === selectOrgId) || null;
      }
      if (!picked && selectByName) {
        const key = selectByName.trim().toLowerCase();
        picked =
          list.find((c) => (c.company_name || "").trim().toLowerCase() === key) ||
          null;
      }
      if (!picked && selectedCompanyId) {
        picked = list.find((c) => c.id === selectedCompanyId) || null;
      }
      if (!picked) {
        picked = list[0];
      }

      setSelectedCompanyId(picked.id);
      applyCompanyFromList(picked);
      return list;
    } catch (e) {
      toast.error(e.message || "Could not load companies");
      return [];
    } finally {
      setCompaniesLoading(false);
    }
  };

  const applyCompanyFromList = (item) => {
    if (!item) return;
    setCompany({
      company_name: item.company_name || "",
      company_logo: item.company_logo || "",
    });
    setLogoPreview(item.company_logo || null);
  };

  useEffect(() => {
    loadCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAllInvites = async () => {
    setInvitesLoading(true);
    try {
      const res = await fetchInvites();
      setAllInvites(res.data?.invites || []);
    } catch {
      setAllInvites([]);
    } finally {
      setInvitesLoading(false);
    }
  };

  useEffect(() => {
    loadAllInvites();
  }, [step, result]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setInsightsLoading(true);
      try {
        const payload = await fetchInsights({ includeScores: false });
        if (!cancelled) {
          setInsightsOrganizations(
            Array.isArray(payload?.data?.organizations) ? payload.data.organizations : []
          );
        }
      } catch {
        if (!cancelled) setInsightsOrganizations([]);
      } finally {
        if (!cancelled) setInsightsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const existingInvitesForCompany = useMemo(() => {
    const key = company.company_name.trim().toLowerCase();
    if (!key) return [];
    return allInvites.filter(
      (r) => (r.company_name || "").trim().toLowerCase() === key
    );
  }, [allInvites, company.company_name]);

  const onSelectExistingCompany = (companyId) => {
    setSelectedCompanyId(companyId);
    const found = companies.find((c) => c.id === companyId);
    applyCompanyFromList(found);
  };

  const switchToExistingCompany = () => {
    setCompanyMode("existing");
    if (companies.length === 0) return;
    const id =
      selectedCompanyId && companies.some((c) => c.id === selectedCompanyId)
        ? selectedCompanyId
        : companies[0].id;
    onSelectExistingCompany(id);
  };

  const switchToNewCompany = () => {
    setCompanyMode("new");
    setCompany({ ...newCompanyDraft });
    setLogoPreview(newCompanyDraft.company_logo || null);
  };

  const updateNewCompanyField = (field, value) => {
    setCompany((prev) => {
      const next = { ...prev, [field]: value };
      setNewCompanyDraft(next);
      return next;
    });
  };

  const onLogoFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file (PNG, JPG, etc.)");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Logo must be 5 MB or smaller");
      return;
    }
    setLogoPreview(URL.createObjectURL(file));
    try {
      const url = await uploadCompanyLogo(file);
      setLogoPreview(url);
      updateNewCompanyField("company_logo", url);
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(err.message || "Could not upload logo");
      setLogoPreview(company.company_logo || null);
    } finally {
      e.target.value = "";
    }
  };

  const clearLogo = () => {
    setLogoPreview(null);
    updateNewCompanyField("company_logo", "");
  };

  const handleNextFromStep1 = async () => {
    setStep1Saving(true);
    try {
      const payload = {
        company_name: company.company_name.trim(),
        ...(company.company_logo ? { company_logo: company.company_logo } : {}),
      };
      const saved = await saveCompany(payload);
      const orgId = saved.data?.org_id;

      if (companyMode === "new") {
        await loadCompanies({
          selectOrgId: orgId,
          selectByName: payload.company_name,
        });
        setCompanyMode("existing");
        toast.success("Company saved — it is now available in the list");
      } else {
        await loadCompanies({ selectOrgId: orgId || selectedCompanyId });
      }

      setStep(2);
    } catch (e) {
      toast.error(e.message || "Could not save company profile");
    } finally {
      setStep1Saving(false);
    }
  };

  const canProceedStep1 =
    companyMode === "new"
      ? !!company.company_name.trim()
      : !!selectedCompanyId && !!company.company_name.trim();

  const updateParticipant = (index, field, value) => {
    setParticipants((list) =>
      list.map((p, i) => {
        if (i !== index) return p;
        const next = { ...p, [field]: value };
        if (field === "email") {
          next.emailError = validateParticipantEmail(value);
        }
        return next;
      })
    );
  };

  const blurParticipantEmail = (index) => {
    setParticipants((list) =>
      list.map((p, i) =>
        i === index
          ? { ...p, emailError: validateParticipantEmail(p.email) }
          : p
      )
    );
  };

  const addRow = () =>
    setParticipants((list) => [...list, emptyParticipant(defaultInviteRole)]);

  const removeRow = (index) => {
    if (participants.length <= 1) return;
    setParticipants((list) => list.filter((_, i) => i !== index));
  };

  const validateParticipants = () => {
    const emails = participants.map((p) => (p.email || "").trim().toLowerCase());
    const duplicateEmails = new Set(
      emails.filter((e, i) => e && emails.indexOf(e) !== i)
    );
    const validated = participants.map((p) => {
      const trimmed = (p.email || "").trim().toLowerCase();
      const fieldError = validateParticipantEmail(p.email);
      const dupeError =
        !fieldError && trimmed && duplicateEmails.has(trimmed)
          ? "Duplicate email in this batch."
          : null;
      return { ...p, emailError: fieldError || dupeError };
    });
    setParticipants(validated);
    return validated.every(isParticipantRowValid);
  };

  /**
   * Email override for this send, or {} to use the saved templates.
   *
   * Returning {} is the important part: with no email_subject/email_html in
   * the request, the backend resolves a template per invite from that
   * recipient's audience (`template_for_invite`), so edits made in
   * Admin → Email Templates & Scheduler are what actually goes out. This step
   * used to post a hardcoded body unconditionally, which silently bypassed
   * those templates.
   *
   * The body is sent unwrapped — the backend wraps it once in
   * invitetemplate.html. The old wrapEmailHtmlForApi() call added a second
   * card around that, producing nested chrome in the delivered email.
   */
  const buildEmailPayload = ({ isResend = false } = {}) => {
    if (!emailCustomized || !editorHtml.trim()) return {};
    // Prefer the saved template's subject over the legacy constant, so a
    // customized body doesn't silently revert the subject line an admin set.
    const defaultAudienceKey = dbAudiences.find((a) => a.is_default)?.key;
    const primary =
      dbTemplates.find((t) => t.role_group === defaultAudienceKey) || dbTemplates[0] || null;
    return {
      email_subject:
        primary?.subject ||
        inviteSubjectForPack(workspace?.email_pack || (workspace?.id ? "generic" : "5x"), {
          isResend,
        }),
      email_html: editorHtml,
    };
  };

  const createInvitesAndSend = async (isProceed = false) => {
    const { email_subject, email_html } = buildEmailPayload();
    const res = await sendBatchOnboarding({
      company_name: company.company_name.trim(),
      pre_survey: preSurvey,
      assessment_mode: assessmentMode,
      offer_lite_report: offerLiteReport,
      send_email: true,
      frontend_origin: window.location.origin,
      is_proceed: isProceed ? "Y" : "N",
      email_subject,
      email_html,
      participants: participants.map((p) => {
        const { first_name, last_name } = splitFullName(p.full_name);
        const designation = p.designation;
        return {
          email: p.email.trim(),
          first_name,
          last_name,
          designation,
          pre_survey: designation === "CEO" ? "Y" : preSurvey,
        };
      }),
    });
    if (res.data?.needs_confirmation) {
      setDuplicateConfirm({
        email: res.data.email || "",
        otherCompanies: res.data.other_companies || [],
        username: res.data.username || "",
        confirmationReason: res.data.confirmation_reason || "other_company",
      });
      return { needsConfirmation: true };
    }
    setDuplicateConfirm(null);
    setResult(res.data);
    setInviteBlockMessage("");
    toast.success(res.message || "Invites sent");
    setStep(3);
    loadCompanies();
  };

  const submitInvites = async () => {
    if (pendingSendInvites) {
      setLoading(true);
      try {
        const { email_subject, email_html } = buildEmailPayload({ isResend: true });
        await resendInviteEmails(
          pendingSendInvites.map((i) => i.invite_id),
          { subject: email_subject, html: email_html, isResend: true }
        );
        toast.success("Reminder emails sent");
        setResult({ invites: pendingSendInvites });
        setStep(3);
        setPendingSendInvites(null);
        loadCompanies();
      } catch (e) {
        toast.error(e.message || "Could not send emails");
      } finally {
        setLoading(false);
      }
      return;
    }

    if (!validateParticipants()) {
      toast.error("Enter each participant’s name, a valid email, and role");
      return;
    }

    const formatError = assessmentFormatRequiredError(companies, {
      companyId: selectedCompanyId,
      companyName: company.company_name,
      workspace,
    });
    if (formatError) {
      setInviteBlockMessage(formatError);
      toast.error(formatError);
      return;
    }
    setInviteBlockMessage("");

    if (selectedCompanyId) {
      try {
        const res = await fetchSurveyTemplates(selectedCompanyId);
        const list = (res.data?.templates || []).filter((t) => !t.is_archived);
        const active = list.find((t) => t.is_active);
        if (active && (active.question_count ?? 0) === 0) {
          const ok = window.confirm(
            "The active assessment for this company has no questions. Send invites anyway?"
          );
          if (!ok) return;
        }
      } catch {
        // Don't block send if template lookup fails
      }
    }

    setLoading(true);
    setResult(null);
    try {
      const outcome = await createInvitesAndSend(false);
      if (outcome?.needsConfirmation) {
        setLoading(false);
        return;
      }
    } catch (e) {
      const msg = e.message || "Could not send invites";
      setInviteBlockMessage(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const confirmDuplicateAndInvite = async () => {
    const formatError = assessmentFormatRequiredError(companies, {
      companyId: selectedCompanyId,
      companyName: company.company_name,
      workspace,
    });
    if (formatError) {
      setInviteBlockMessage(formatError);
      toast.error(formatError);
      return;
    }
    setLoading(true);
    try {
      await createInvitesAndSend(true);
    } catch (e) {
      toast.error(e.message || "Could not send invites");
    } finally {
      setLoading(false);
      setDuplicateConfirm(null);
    }
  };

  const resetForm = () => {
    setStep(flatWorkspace ? 2 : 1);
    setResult(null);
    setPendingSendInvites(null);
    setParticipants([emptyParticipant(defaultInviteRole)]);
    setEditorHtml("");
    setEmailCustomized(false);
    setEmailExpanded(false);
    setPreviewTemplate(null);
    setDuplicateConfirm(null);
    setCopiedInviteId(null);
    const defs = workspaceInviteDefaults(workspace);
    if (litePathAvailable) {
      setAssessmentMode(defs.assessment_mode);
      setOfferLiteReport(defs.offer_lite_report);
    } else {
      setAssessmentMode(ASSESSMENT_MODE_FULL_ONLY);
      setOfferLiteReport("N");
    }
    loadCompanies();
  };

  const recipientEmails = pendingSendInvites
    ? pendingSendInvites.map((i) => i.email).filter(Boolean)
    : participants.map((p) => p.email?.trim()).filter(Boolean);

  const assistantContext = useMemo(
    () =>
      buildInviteWizardContext({
        step,
        companyMode,
        companyName: company.company_name,
        selectedCompanyId,
        companyHasLogo: !!company.company_logo,
        newCompanyDraft,
        preSurvey,
        emailCustomized,
        editorHtml,
        emailExpanded,
        participants,
        pendingSendInvites,
        result,
        duplicateConfirm,
        companiesCatalog: companies,
        companiesLoading,
        allInvites,
        invitesLoading,
        existingInvitesForCompany,
        insightsOrganizations,
        insightsLoading,
        loading,
        participantRoles: inviteRoles,
        workspace,
        step1Saving,
        bulkModalOpen: bulkOpen,
        canProceedStep1,
        recipientEmails,
        assessmentMode,
        offerLiteReport,
        litePathAvailable,
      }),
    [
      step,
      companyMode,
      company.company_name,
      company.company_logo,
      selectedCompanyId,
      newCompanyDraft,
      preSurvey,
      emailCustomized,
      editorHtml,
      emailExpanded,
      participants,
      pendingSendInvites,
      result,
      duplicateConfirm,
      companies,
      companiesLoading,
      allInvites,
      invitesLoading,
      existingInvitesForCompany,
      insightsOrganizations,
      insightsLoading,
      loading,
      step1Saving,
      bulkOpen,
      canProceedStep1,
      recipientEmails,
      inviteRoles,
      workspace,
      assessmentMode,
      offerLiteReport,
      litePathAvailable,
    ]
  );

  useEffect(() => {
    if (!setAdminAssistantContext) return;
    setAdminAssistantContext(assistantContext);
  }, [assistantContext, setAdminAssistantContext]);

  useEffect(() => {
    return () => setAdminAssistantContext?.(null);
  }, [setAdminAssistantContext]);

  const stepLabels = flatWorkspace
    ? ["Participants", "Done"]
    : ["Company", "Participants", "Done"];

  return (
    <div className={embedded ? "nexus-surface-embed" : "min-h-full w-full bg-white"}>
      <div className={embedded ? "w-full" : "w-full px-6 py-6"}>
        {!embedded ? (
        <header className="mb-6">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-[#004f73]/70 mb-2">
            Admin
          </p>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Invite participants</h1>
              <p className="text-sm text-slate-500 mt-1">
                Set up a company, add participants, and send customized invitation emails.
              </p>
            </div>
            <Link
              to={LENS_ROUTES.engagementPipeline}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg transition-colors shrink-0"
            >
              View invited list
            </Link>
          </div>
        </header>
        ) : null}

        <div className={embedded ? "w-full max-w-3xl" : "max-w-3xl"}>
      <div className="flex gap-2 mb-8 text-xs font-medium flex-wrap">
        {stepLabels.map((label, i) => {
          const stepNum = flatWorkspace ? i + 2 : i + 1;
          return (
          <span
            key={label}
            className={`px-3 py-1 rounded-full ${
              step === stepNum
                ? "bg-brand text-white"
                : step > stepNum
                  ? "bg-brand/20 text-brand"
                  : "bg-gray-200 text-gray-600"
            }`}
          >
            {i + 1}. {label}
          </span>
          );
        })}
      </div>

      {step === 1 && !flatWorkspace && (
        <div className="bg-white rounded-xl border p-6 space-y-4">
          <h2 className="font-semibold text-brand">Company Profile</h2>

          <div className="flex flex-col sm:flex-row gap-4 text-sm">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="companyMode"
                checked={companyMode === "existing"}
                disabled={companies.length === 0}
                onChange={switchToExistingCompany}
              />
              Select existing company
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="companyMode"
                checked={companyMode === "new"}
                onChange={switchToNewCompany}
              />
              Add new company
            </label>
          </div>

          {companiesLoading ? (
            <p className="text-sm text-gray-500">Loading companies…</p>
          ) : companyMode === "existing" ? (
            <>
              {companies.length === 0 ? (
                <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  No companies yet. Choose &quot;Add new company&quot; to create one.
                </p>
              ) : (
                <div className="flex flex-col sm:flex-row gap-5 items-start sm:items-end">
                  <div className="w-full sm:w-[36rem] sm:max-w-2xl min-w-0">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Company <span className="text-red-600">*</span>
                    </label>
                    <select
                      className="admin-filter-select w-full border rounded-lg px-3 py-2"
                      value={selectedCompanyId}
                      onChange={(e) => onSelectExistingCompany(e.target.value)}
                    >
                      {companies.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.company_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {company.company_logo ? (
                    <div className="shrink-0 flex flex-col items-center">
                      <span className="text-xs font-medium text-gray-500 mb-1">Logo</span>
                      <img
                        src={company.company_logo}
                        alt=""
                        className="h-20 w-20 object-contain border rounded-lg bg-white p-1.5"
                      />
                    </div>
                  ) : null}
                </div>
              )}
            </>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company name <span className="text-red-600">*</span>
                </label>
                <input
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="Enter company name"
                  value={company.company_name}
                  onChange={(e) => updateNewCompanyField("company_name", e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company logo <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <div className="flex flex-col sm:flex-row gap-4 items-start">
                  <label className="flex flex-col items-center justify-center w-full sm:w-48 h-28 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 bg-gray-50/50">
                    <span className="text-xs text-gray-600 text-center px-2">
                      Click to upload image
                    </span>
                    <span className="text-[10px] text-gray-400 mt-1">PNG, JPG · max 2 MB</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={onLogoFileChange}
                    />
                  </label>
                  {logoPreview ? (
                    <div className="relative">
                      <img
                        src={logoPreview}
                        alt="Logo preview"
                        className="h-28 max-w-[12rem] object-contain border rounded-lg bg-white p-2"
                      />
                      <button
                        type="button"
                        className="absolute -top-2 -right-2 bg-white border rounded-full w-6 h-6 text-xs text-gray-600 hover:text-red-600 shadow"
                        onClick={clearLogo}
                        title="Remove logo"
                      >
                        ×
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          )}

          <button
            type="button"
            className="btn-primary"
            disabled={!canProceedStep1 || step1Saving}
            onClick={handleNextFromStep1}
          >
            {step1Saving ? "Saving company…" : "Next: Participants"}
          </button>
        </div>
      )}

      {flatWorkspace && step === 2 ? (
        <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
          <p className="font-medium text-slate-900">Organization</p>
          <p className="text-xs text-slate-600 mt-0.5">
            Invites apply to{" "}
            <span className="font-medium">{company.company_name || workspace?.product_display_name}</span>
            . This workspace runs without a separate company portfolio.
          </p>
        </div>
      ) : null}

      {step === 2 && (
        <div className="bg-white rounded-xl border p-6 space-y-4">
          <div className="flex items-start justify-between gap-3">
            <h2 className="font-semibold text-brand">Invite Participants</h2>
            <button
              type="button"
              className="blue_btn text-sm shrink-0 px-3 py-1.5"
              onClick={() => {
                if (!company.company_name.trim()) {
                  toast.error("Complete company setup in step 1 first");
                  return;
                }
                setBulkOpen(true);
              }}
            >
              Bulk Invite
            </button>
          </div>

          {pendingSendInvites ? (
            <div className="space-y-3">
              <p className="text-sm text-blue-800 bg-blue-50 border border-blue-200 rounded-lg p-3">
                {pendingSendInvites.length} participant(s) created from bulk upload. Review
                below, choose an email template, and send.
              </p>
              <div className="max-h-48 overflow-auto border rounded-lg text-sm">
                <table className="w-full">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="p-2 text-left font-medium text-gray-700">Name</th>
                      <th className="p-2 text-left font-medium text-gray-700">Email</th>
                      <th className="p-2 text-left font-medium text-gray-700">Role</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingSendInvites.map((inv) => (
                      <tr key={inv.invite_id} className="border-t border-gray-100">
                        <td className="p-2 text-gray-900">
                          {[inv.first_name, inv.last_name].filter(Boolean).join(" ") || "—"}
                        </td>
                        <td className="p-2 text-[#004f73] break-all">{inv.email || "—"}</td>
                        <td className="p-2 text-gray-700">{inv.designation || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <>
              {participants.map((p, i) => (
                <div key={i} className="border-b pb-4 space-y-3 last:border-b-0">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Name <span className="text-red-600">*</span>
                      </label>
                      <input
                        className="w-full border rounded-lg px-3 py-2"
                        placeholder="Full name"
                        value={p.full_name}
                        onChange={(e) => updateParticipant(i, "full_name", e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email <span className="text-red-600">*</span>
                      </label>
                      <input
                        className={`w-full border rounded-lg px-3 py-2 ${
                          p.emailError ? "border-red-500" : ""
                        }`}
                        placeholder="email@example.com"
                        type="email"
                        autoComplete="email"
                        value={p.email}
                        onChange={(e) => updateParticipant(i, "email", e.target.value)}
                        onBlur={() => blurParticipantEmail(i)}
                      />
                      {p.emailError ? (
                        <p className="text-red-600 text-xs mt-1">{p.emailError}</p>
                      ) : null}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Role <span className="text-red-600">*</span>
                    </label>
                    <InviteRoleField
                      roleOptions={inviteRoles}
                      value={p.designation}
                      onChange={(v) => updateParticipant(i, "designation", v)}
                    />
                  </div>
                  {participants.length > 1 ? (
                    <button
                      type="button"
                      className="text-red-600 text-sm"
                      onClick={() => removeRow(i)}
                    >
                      Remove participant
                    </button>
                  ) : null}
                </div>
              ))}
              <button type="button" className="btn-secondary text-sm" onClick={addRow}>
                + Add participant
              </button>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={preSurvey === "Y"}
                  onChange={(e) => setPreSurvey(e.target.checked ? "Y" : "N")}
                />
                Include pre-survey for non-CEO roles (CEOs always receive it)
              </label>
              <InviteAssessmentOptions
                litePathAvailable={litePathAvailable}
                assessmentMode={assessmentMode}
                offerLiteReport={offerLiteReport}
                assessmentMode={assessmentMode}
                offerLiteReport={offerLiteReport}
                onAssessmentModeChange={(mode) => {
                  setAssessmentMode(mode);
                  setOfferLiteReport(mode === ASSESSMENT_MODE_FULL_ONLY ? "N" : "Y");
                }}
                onOfferLiteReportChange={setOfferLiteReport}
              />
            </>
          )}

          {inviteBlockMessage ? (
            <div
              className="mb-4 rounded-lg border border-red-300 bg-red-50 text-red-900 px-4 py-3 text-sm dark:border-red-800 dark:bg-red-950/40 dark:text-red-100"
              role="alert"
            >
              {inviteBlockMessage}
            </div>
          ) : null}

          <WizardInviteEmailSection
            participants={roleSource}
            recipientEmails={recipientEmails}
            templates={dbTemplates}
            audiences={dbAudiences}
            customized={emailCustomized}
            onCustomizedChange={setEmailCustomized}
            editorHtml={editorHtml}
            onEditorChange={setEditorHtml}
            expanded={emailExpanded}
            onToggleExpanded={setEmailExpanded}
            onPreviewTemplate={(templateId, audienceLabel) =>
              setPreviewTemplate({ templateId, audienceLabel })
            }
            isResend={!!pendingSendInvites}
            sendDisabled={
              loading ||
              (!pendingSendInvites && !participants.some(isParticipantRowValid))
            }
            sending={loading}
            sendLabel={pendingSendInvites ? "Send invitation emails" : "Send invites"}
            onSend={submitInvites}
          />

          {previewTemplate ? (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
              <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-white rounded-xl shadow-xl">
                <TemplatePreviewPanel
                  templateId={previewTemplate.templateId}
                  audienceLabel={previewTemplate.audienceLabel}
                  onClose={() => setPreviewTemplate(null)}
                />
              </div>
            </div>
          ) : null}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                setPendingSendInvites(null);
                setStep(flatWorkspace ? 2 : 1);
              }}
            >
              Back
            </button>
          </div>
        </div>
      )}

      {step === 3 && result && (
        <div className="bg-white rounded-xl border p-6 space-y-4">
          <h2 className="font-semibold text-green-700">Invites sent</h2>
          <ul className="text-sm space-y-3 max-h-64 overflow-auto">
            {(result.invites || []).map((inv) => (
              <li
                key={inv.invite_id}
                className="border rounded-lg p-3 flex flex-wrap items-center justify-between gap-2"
              >
                <p className="font-medium">
                  {inv.first_name} {inv.last_name} — {inv.email}
                </p>
                {inv.survey_token ? (
                  <button
                    type="button"
                    className="btn-secondary text-xs shrink-0 min-w-[5.5rem]"
                    onClick={() =>
                      copyLink(
                        participantAssessmentUrl(inv.survey_token),
                        inv.invite_id
                      )
                    }
                  >
                    {copiedInviteId === inv.invite_id ? "Copied" : "Copy link"}
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
          <button type="button" className="btn-primary" onClick={resetForm}>
            {flatWorkspace ? "Send more invites" : "Invite another company"}
          </button>
        </div>
      )}

      <DuplicateEmailConfirmDialog
        open={!!duplicateConfirm}
        email={duplicateConfirm?.email}
        otherCompanies={duplicateConfirm?.otherCompanies}
        username={duplicateConfirm?.username}
        targetCompanyName={company.company_name.trim()}
        confirmationReason={duplicateConfirm?.confirmationReason}
        busy={loading}
        onConfirm={confirmDuplicateAndInvite}
        onCancel={() => {
          setDuplicateConfirm(null);
          setLoading(false);
        }}
      />

      <BulkInviteModal
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        companyName={company.company_name}
        lockCompany
        roleOptions={inviteRoles}
        sampleCsv={sampleCsv}
        onSuccess={({ invites }) => {
          setBulkOpen(false);
          setPendingSendInvites(invites || []);
          // No template pre-selection: each invite resolves its own audience
          // template server-side unless the admin explicitly customizes.
          setEmailCustomized(false);
          setEditorHtml("");
          setStep(2);
          toast.success("Bulk invites created — review email and send.");
        }}
      />
        </div>
      </div>
    </div>
  );
}
